import type { JSX } from 'react';
import type { RunPoint } from '../api/types';
import { formatRunDate } from './model';

const BAR_W = 7;
const GAP = 2;
const HEIGHT = 28;
/** A run that added nobody still happened — it keeps a stub on the baseline. */
const STUB = 2;

function describe(r: RunPoint): string {
  const when = formatRunDate(r.runAt);
  if (r.status === 'failed') return `${when}: failed`;
  if (r.status === 'running') return `${when}: running, ${r.added} added so far`;
  return `${when}: ${r.added} added${r.status === 'stopped' ? ' (stopped)' : ''}`;
}

/**
 * Creators added per recent run, oldest → newest, drawn to scale within the
 * schedule (the tallest bar is its best recent run; the number beside the
 * strip says what that is, so rows can still be compared).
 *
 * One series, one hue: bars use the accent. State is the only other colour —
 * a failed run is a destructive stub, a run still in flight is faded — and
 * every bar carries its date and count on hover, so nothing is colour-alone.
 * The run history sheet is the table view of the same data.
 */
export function RunSparkline({ runs, slots = 10 }: { runs: RunPoint[]; slots?: number }): JSX.Element {
  if (runs.length === 0) {
    return <span className="text-xs text-muted-foreground">No runs recorded</span>;
  }
  const shown = runs.slice(-slots);
  const peak = Math.max(0, ...shown.map((r) => r.added));
  const max = Math.max(1, peak);
  const width = slots * (BAR_W + GAP) - GAP;
  // Right-aligned: the newest run always sits in the same place.
  const offset = (slots - shown.length) * (BAR_W + GAP);
  const total = shown.reduce((n, r) => n + r.added, 0);

  return (
    <span className="inline-flex items-end gap-2">
      <svg
        role="img"
        aria-label={`Added per run, last ${shown.length} run${shown.length === 1 ? '' : 's'}: ${shown.map((r) => (r.status === 'failed' ? 'failed' : r.added)).join(', ')}`}
        width={width}
        height={HEIGHT}
        viewBox={`0 0 ${width} ${HEIGHT}`}
        className="shrink-0 overflow-visible"
        data-testid="run-sparkline"
      >
        <line x1={0} x2={width} y1={HEIGHT - 0.5} y2={HEIGHT - 0.5} className="stroke-border" strokeWidth={1} />
        {shown.map((r, i) => {
          const x = offset + i * (BAR_W + GAP);
          const failed = r.status === 'failed';
          const h = failed || r.added === 0 ? STUB : Math.max(STUB + 1, Math.round((r.added / max) * (HEIGHT - 2)));
          return (
            <g key={`${r.jobId}-${i}`} data-run-status={r.status} data-added={r.added}>
              <title>{describe(r)}</title>
              {/* Full-height hit area: the hover target is bigger than the mark. */}
              <rect x={x - GAP / 2} y={0} width={BAR_W + GAP} height={HEIGHT} fill="transparent" />
              <rect
                x={x}
                y={HEIGHT - 1 - h}
                width={BAR_W}
                height={h}
                rx={Math.min(2, h / 2)}
                className={
                  failed
                    ? 'fill-destructive'
                    : r.added === 0
                      ? 'fill-muted-foreground/45'
                      : r.status === 'running'
                        ? 'fill-brand/45'
                        : 'fill-brand'
                }
              />
            </g>
          );
        })}
      </svg>
      <span className="whitespace-nowrap text-xs tabular-nums text-muted-foreground" title={`${total} added across these runs`}>
        peak {peak.toLocaleString()}
      </span>
    </span>
  );
}
